import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import { cn } from '../../lib/cn.js';
import { useLocale } from '../../i18n/locale-context.js';
const ToastContext = React.createContext(undefined);
const VARIANT_CLASSES = {
    success: 'border-status-good-solid bg-status-good-tint-bg text-status-good-tint-fg',
    error: 'border-status-critical-solid bg-status-critical-tint-bg text-status-critical-tint-fg',
    warning: 'border-status-warning-solid bg-status-warning-tint-bg text-status-warning-tint-fg',
    info: 'border-status-info-solid bg-status-info-tint-bg text-status-info-tint-fg',
};
/** Success auto-dismisses (5s); error/warning persist until the user dismisses them (§6 #22). */
const AUTO_DISMISS_MS = {
    success: 5000,
    info: 5000,
    warning: undefined,
    error: undefined,
};
/**
 * ux-review defect (this pass): the whole point of a toast is that it can be perceived
 * without the toast itself stealing keyboard/visual focus, but that only works for
 * assistive tech if the live-region role actually matches the urgency — a success
 * confirmation is `role="status"` (polite: announced without interrupting), a failure
 * that needs the user's attention is `role="alert"` (assertive). Radix's own `Root`
 * renders a generic live region and leaves the ARIA `role` to the caller; this was
 * previously unset entirely, so every toast (success or error alike) announced with
 * whatever the browser's default fallback happened to be — never verified, never
 * required by any test. `warning` groups with `error` here because both persist until
 * dismissed (`AUTO_DISMISS_MS` above) — an unread, un-auto-dismissed message is exactly
 * the case `alert`'s assertive announcement exists for.
 */
const VARIANT_ROLE = {
    success: 'status',
    info: 'status',
    warning: 'alert',
    error: 'alert',
};
/**
 * DESIGN_SYSTEM.md §6 #22. "Auto-dismisses only for `success`; `error` and `warning`
 * persist until dismissed, because a citizen/officer must not lose an error before
 * reading it." Mount one `ToastProvider` at the app root; call `useToast().push(...)`
 * anywhere beneath it.
 */
export function ToastProvider({ children }) {
    const { t } = useLocale();
    const [toasts, setToasts] = React.useState([]);
    const push = React.useCallback((toast) => {
        setToasts((prev) => [...prev, { ...toast, id: crypto.randomUUID() }]);
    }, []);
    const remove = React.useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    const value = React.useMemo(() => ({ push }), [push]);
    return (_jsx(ToastContext.Provider, { value: value, children: _jsxs(RadixToast.Provider, { swipeDirection: "right", children: [children, toasts.map((toast) => (_jsxs(RadixToast.Root, { role: VARIANT_ROLE[toast.variant], duration: AUTO_DISMISS_MS[toast.variant] ?? Infinity, onOpenChange: (open) => {
                        if (!open)
                            remove(toast.id);
                    }, className: cn('rounded-md border-l-4 bg-surface-elevated p-4 shadow-elevation-2', VARIANT_CLASSES[toast.variant]), children: [_jsx(RadixToast.Title, { className: "font-semibold", children: toast.title }), toast.description ? (_jsx(RadixToast.Description, { className: "mt-1 text-sm", children: toast.description })) : null, _jsx(RadixToast.Close, { "aria-label": t('common.dismiss'), className: "mt-2 text-sm font-semibold underline", children: t('common.dismiss') })] }, toast.id))), _jsx(RadixToast.Viewport, { className: "fixed bottom-4 right-4 z-[100] flex w-[min(24rem,90vw)] flex-col gap-2" })] }) }));
}
export function useToast() {
    const context = React.useContext(ToastContext);
    if (!context)
        throw new Error('useToast must be used within a <ToastProvider>.');
    return context;
}
//# sourceMappingURL=Toast.js.map